import { Fragment, useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  CheckCircle2Icon,
  ClockIcon,
  CreditCardIcon,
} from "lucide-react";
import {
  Button,
  EmptyState,
  ErrorNotice,
  Panel,
  Pill,
  SkeletonTable,
  StripePayment,
  TextInput,
} from "@bcecloud/portal-ui";
import { getToken } from "@/lib/auth";
import { formatCalendarDate } from "@/lib/dates";
import { HowItWorks, PAYING_HELP } from "@/components/HowItWorks";
import { useEventContext } from "./useEventContext";

/** One payable line of the schedule, as the payment endpoint returns it. */
interface ScheduleLine {
  key: "deposit" | "balance" | "full";
  label: string;
  amount: number;
  outstanding: number;
  paid: boolean;
  dueDate: string | null;
  dueNote: string | null;
}

/**
 * What's being paid right now: one schedule line, the whole outstanding
 * balance (`total`), or a custom partial amount. Carries the display amount;
 * only `custom` sends it to the API (which re-validates it either way).
 */
interface PayScope {
  line: ScheduleLine["key"] | "total" | "custom";
  amount: number;
}

/** One recorded payment (Stripe ledger or a Salesforce/legacy transaction). */
interface LedgerItem {
  id: string;
  type: string;
  amount: number;
  date: string | null;
  source?: "stripe" | "salesforce";
}

/** The payment endpoint's response. */
interface PaymentData {
  publishableKey: string | null;
  /** The credit-card surcharge, said up front; 0 = none. */
  cardSurchargePercent?: number;
  currency: string;
  schedule: {
    contractedAmount: number;
    amountPaid: number;
    amountDue: number;
    lines: ScheduleLine[];
  };
  payments: LedgerItem[];
}

/** Formats a number as USD currency. */
function formatCurrency(amount?: number): string {
  if (amount === undefined || amount === null) return "$0.00";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
  }).format(amount);
}

/** Formats an ISO date for display, e.g. "Sep 10, 2026". */
function formatDate(dateStr?: string | null): string {
  if (!dateStr) return "—";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function authHeaders(): { Authorization: string } {
  return { Authorization: `Bearer ${getToken()}` };
}

/** The request body's "what to pay" part: a line, everything, or a custom amount. */
function payBody(scope: PayScope): { line: string; amount?: number } {
  return { line: scope.line, ...(scope.line === "custom" ? { amount: scope.amount } : {}) };
}

/**
 * The shared BCE payment form (portal-ui's StripePayment) for one pay scope:
 * bank or card up front, a credit-card surcharge shown before it's charged,
 * charged on Pay. My Events reaches the API as the signed-in event contact.
 */
function InlinePayment({
  eventId,
  scope,
  publishableKey,
  cardSurchargePercent,
  onPaid,
}: {
  eventId: string;
  scope: PayScope;
  publishableKey: string;
  cardSurchargePercent: number;
  onPaid: (status: string) => void;
}): React.ReactElement {
  const base = `/api/v3/events/${eventId}/payment`;
  return (
    <StripePayment
      publishableKey={publishableKey}
      amountCents={Math.round(scope.amount * 100)}
      amountLabel={formatCurrency(scope.amount)}
      cardSurchargePercent={cardSurchargePercent}
      formatMoney={(dollars) => formatCurrency(dollars)}
      accentColor="#14213d"
      quote={async (confirmationTokenId) => {
        const { data } = await axios.post(`${base}/quote`, { ...payBody(scope), confirmationTokenId }, { headers: authHeaders() });
        return data.data;
      }}
      pay={async (confirmationTokenId, expectedTotalCents) => {
        const { data } = await axios.post(
          `${base}/pay`,
          { ...payBody(scope), confirmationTokenId, expectedTotalCents },
          { headers: authHeaders() },
        );
        return data.data;
      }}
      confirmPayment={async (paymentIntentId) => {
        const { data } = await axios.post(`${base}/${paymentIntentId}/confirm`, {}, { headers: authHeaders() });
        return (data.data as { status: string }).status;
      }}
      onComplete={onPaid}
    />
  );
}

/**
 * Invoice & payment tab — the invoice with per-line "Pay now" (embedded
 * Stripe Payment Element) beside the Stripe payment history. Contract totals
 * come from the event; what's been paid comes from the isolated Stripe ledger.
 */
export function EventPaymentTab(): React.ReactElement | null {
  const { event } = useEventContext();
  const eventId = event?._id;

  const [data, setData] = useState<PaymentData | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [paying, setPaying] = useState<PayScope | null>(null);
  // The "Make a payment" chooser: everything outstanding, or a custom amount.
  const [payChoice, setPayChoice] = useState<"full" | "custom">("full");
  const [customAmount, setCustomAmount] = useState("");
  const [customError, setCustomError] = useState<string | null>(null);
  const [processingNotice, setProcessingNotice] = useState(false);

  const load = useCallback(async (): Promise<void> => {
    if (!eventId) return;
    try {
      const { data: res } = await axios.get(
        `/api/v3/events/${eventId}/payment`,
        {
          headers: authHeaders(),
        },
      );
      setData(res.data as PaymentData);
      setError(null);
    } catch {
      setError("We could not load your invoice. Please try again.");
    }
  }, [eventId]);

  useEffect(() => {
    void load();
  }, [load]);

  if (!event) return null;
  if (error) return <ErrorNotice title="Invoice unavailable" message={error} />;
  if (data === null) {
    return (
      <SkeletonTable
        rows={3}
        columns={["w-[30%]", "w-[15%]"]}
        label="Loading invoice…"
      />
    );
  }

  const { schedule, payments, publishableKey } = data;
  const cardSurchargePercent = data.cardSurchargePercent ?? 0;
  const isPaidInFull = schedule.amountDue <= 0 && schedule.amountPaid > 0;

  if (schedule.contractedAmount <= 0 && payments.length === 0) {
    return (
      <Panel>
        <EmptyState
          icon={<CreditCardIcon aria-hidden className="size-6" />}
          title="No payment information"
          message="Payment details are not available for this event."
        />
      </Panel>
    );
  }

  async function onPaid(status: string): Promise<void> {
    setPaying(null);
    setCustomAmount("");
    // ACH debits settle in a few business days: the intent is 'processing',
    // nothing lands in the ledger yet, so tell the payer what to expect.
    setProcessingNotice(status === "processing");
    await load();
  }

  /** The chooser's Continue: open the payment form for the chosen amount. */
  function continueToPayment(): void {
    if (payChoice === "full") {
      setCustomError(null);
      setPaying({ line: "total", amount: schedule.amountDue });
      return;
    }
    const amount = Math.round(Number(customAmount) * 100) / 100;
    if (!Number.isFinite(amount) || amount < 1) {
      setCustomError("Enter an amount of at least $1.");
      return;
    }
    if (amount > schedule.amountDue) {
      setCustomError(
        `That's more than the remaining balance (${formatCurrency(schedule.amountDue)}).`,
      );
      return;
    }
    setCustomError(null);
    setPaying({ line: "custom", amount });
  }

  const showPaySection = Boolean(publishableKey) && schedule.amountDue >= 1;
  const payingBelow =
    paying !== null && (paying.line === "total" || paying.line === "custom");

  return (
    <div className="space-y-4">
      {processingNotice ? (
        <Panel className="border-l-4 border-l-portal-brand-accent px-4 py-3">
          <p className="text-portal-small font-bold">Bank payment initiated</p>
          <p className="mt-0.5 text-portal-tiny text-portal-muted">
            ACH debits take a few business days to clear. Your payment will
            appear in the history once it settles — no need to pay again.
          </p>
        </Panel>
      ) : null}

      <HowItWorks {...PAYING_HELP} />

      <div className="grid gap-6 lg:grid-cols-3">
        {/* Invoice */}
        <div className="lg:col-span-2">
          <Panel className="overflow-hidden p-0">
            <div className="border-b border-portal-line bg-portal-ink px-6 py-5 text-white">
              <div className="flex items-start justify-between gap-4">
                <div>
                  <p className="text-portal-micro font-extrabold tracking-[0.6px] text-portal-brand-accent uppercase">
                    Invoice
                  </p>
                  <p className="mt-1 text-portal-card-title font-bold text-white">
                    {event.contractNumber}
                  </p>
                  <p className="mt-0.5 text-portal-small text-white/70">
                    {event.programDisplayName || event.programName}
                  </p>
                  {event.account?.name ? (
                    <p className="text-portal-small text-white/70">
                      {event.account.name}
                    </p>
                  ) : null}
                </div>
                {isPaidInFull ? (
                  <Pill tone="success">Paid in full</Pill>
                ) : schedule.amountDue > 0 ? (
                  <Pill tone="warning">Balance due</Pill>
                ) : null}
              </div>
            </div>

            <div className="overflow-x-auto [contain:layout]">
              <table
                className="w-full text-left text-portal-tiny"
                style={{ minWidth: 560 }}
              >
                <thead>
                  <tr className="bg-[#f2f5f9] text-portal-micro text-[#7a8699] uppercase">
                    <th scope="col" className="p-3 font-extrabold">
                      Description
                    </th>
                    <th scope="col" className="p-3 font-extrabold">
                      Due
                    </th>
                    <th scope="col" className="p-3 font-extrabold">
                      Status
                    </th>
                    <th scope="col" className="p-3 text-right font-extrabold">
                      Amount
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {schedule.lines.map((line) => {
                    const payable =
                      !line.paid &&
                      line.outstanding > 0 &&
                      Boolean(publishableKey);
                    const isPaying = paying?.line === line.key;
                    return (
                      <Fragment key={line.key}>
                        <tr className="border-t border-portal-line align-middle">
                          <td className="px-4 py-3">
                            <span className="block text-portal-small font-bold">
                              {line.label}
                            </span>
                            <span className="block text-portal-micro text-portal-muted">
                              {event.programDisplayName || event.programName}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-portal-muted">
                            {line.dueDate
                              ? formatCalendarDate(line.dueDate)
                              : (line.dueNote ?? "—")}
                          </td>
                          <td className="px-4 py-3">
                            {line.paid ? (
                              <span className="inline-flex items-center gap-1 text-portal-micro font-bold text-portal-success">
                                <CheckCircle2Icon
                                  aria-hidden
                                  className="size-3.5"
                                />{" "}
                                Paid
                              </span>
                            ) : payable ? (
                              <Button
                                size="compact"
                                variant={isPaying ? "secondary" : "primary"}
                                onClick={() =>
                                  setPaying(
                                    isPaying
                                      ? null
                                      : {
                                          line: line.key,
                                          amount: line.outstanding,
                                        },
                                  )
                                }
                              >
                                {isPaying ? "Cancel" : "Pay now"}
                              </Button>
                            ) : (
                              <span className="text-portal-micro font-bold text-amber-600">
                                {formatCurrency(line.outstanding)} due
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-right font-bold tabular-nums">
                            {formatCurrency(line.amount)}
                          </td>
                        </tr>
                        {isPaying && paying && eventId ? (
                          <tr className="border-t border-portal-line">
                            <td colSpan={4} className="bg-portal-bg px-4 py-4">
                              <InlinePayment
                                eventId={eventId}
                                scope={paying}
                                publishableKey={publishableKey as string}
                                cardSurchargePercent={cardSurchargePercent}
                                onPaid={(status) => void onPaid(status)}
                              />
                            </td>
                          </tr>
                        ) : null}
                      </Fragment>
                    );
                  })}
                </tbody>
                <tfoot className="border-t border-portal-line">
                  <tr>
                    <td
                      colSpan={3}
                      className="px-4 pt-4 pb-1 text-right text-portal-small font-bold"
                    >
                      Contract total
                    </td>
                    <td className="px-4 pt-4 pb-1 text-right text-portal-card-title font-bold tabular-nums">
                      {formatCurrency(schedule.contractedAmount)}
                    </td>
                  </tr>
                  <tr>
                    <td
                      colSpan={3}
                      className="px-4 py-1 text-right text-portal-tiny text-portal-muted"
                    >
                      Total paid
                    </td>
                    <td className="px-4 py-1 text-right font-bold text-portal-success tabular-nums">
                      {formatCurrency(schedule.amountPaid)}
                    </td>
                  </tr>
                  <tr>
                    <td
                      colSpan={3}
                      className={`px-4 pt-1 pb-4 text-right text-portal-tiny font-bold ${schedule.amountDue > 0 ? "text-amber-600" : "text-portal-muted"}`}
                    >
                      Balance due
                    </td>
                    <td
                      className={`px-4 pt-1 pb-4 text-right text-portal-card-title font-bold tabular-nums ${schedule.amountDue > 0 ? "text-amber-600" : ""}`}
                    >
                      {formatCurrency(schedule.amountDue)}
                    </td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {showPaySection ? (
              <div className="border-t border-portal-line bg-portal-bg px-4 py-4">
                <div className="flex items-center justify-between gap-4">
                  <h3 className="text-portal-small font-bold">
                    {payingBelow && paying
                      ? `Paying ${formatCurrency(paying.amount)} toward your balance`
                      : "Make a payment"}
                  </h3>
                  {payingBelow ? (
                    <Button
                      size="compact"
                      variant="secondary"
                      onClick={() => setPaying(null)}
                    >
                      Cancel
                    </Button>
                  ) : null}
                </div>

                {!payingBelow ? (
                  <form
                    className="mt-3"
                    onSubmit={(e: React.FormEvent) => {
                      e.preventDefault();
                      continueToPayment();
                    }}
                  >
                    <fieldset className="space-y-2">
                      <legend className="sr-only">How much to pay</legend>
                      <label className="flex cursor-pointer items-center gap-2 text-portal-small">
                        <input
                          type="radio"
                          name="pay-choice"
                          className="size-4 accent-portal-ink"
                          checked={payChoice === "full"}
                          onChange={() => {
                            setPayChoice("full");
                            setCustomError(null);
                          }}
                        />
                        <span>
                          Pay the full balance —{" "}
                          <strong className="tabular-nums">
                            {formatCurrency(schedule.amountDue)}
                          </strong>
                        </span>
                      </label>
                      <label className="flex cursor-pointer flex-wrap items-center gap-2 text-portal-small">
                        <input
                          type="radio"
                          name="pay-choice"
                          className="size-4 accent-portal-ink"
                          checked={payChoice === "custom"}
                          onChange={() => setPayChoice("custom")}
                        />
                        <span>Pay another amount</span>
                        {payChoice === "custom" ? (
                          <TextInput
                            aria-label="Amount in US dollars"
                            density="compact"
                            className="w-36"
                            type="number"
                            inputMode="decimal"
                            min={1}
                            max={schedule.amountDue}
                            step="0.01"
                            placeholder="1,000.00"
                            value={customAmount}
                            onChange={(
                              e: React.ChangeEvent<HTMLInputElement>,
                            ) => setCustomAmount(e.target.value)}
                          />
                        ) : null}
                      </label>
                    </fieldset>
                    {payChoice === "custom" ? (
                      <p className="mt-1.5 text-portal-micro text-portal-muted">
                        Pay any amount up to{" "}
                        {formatCurrency(schedule.amountDue)} — you can pay the
                        rest anytime.
                      </p>
                    ) : null}
                    {customError ? (
                      <p className="mt-2 text-portal-micro font-bold text-rose-600">
                        {customError}
                      </p>
                    ) : null}
                    <Button size="compact" type="submit" className="mt-3">
                      Continue to payment
                    </Button>
                  </form>
                ) : null}

                {payingBelow && paying && eventId ? (
                  <div className="mt-4">
                    <InlinePayment
                      eventId={eventId}
                      scope={paying}
                      publishableKey={publishableKey as string}
                      cardSurchargePercent={cardSurchargePercent}
                      onPaid={(status) => void onPaid(status)}
                    />
                  </div>
                ) : null}
              </div>
            ) : null}
          </Panel>
        </div>

        {/* Payment history */}
        <div>
          <Panel className="overflow-hidden p-0">
            <div className="border-b border-portal-line px-6 py-4">
              <h2 className="text-portal-section font-bold tracking-[-0.4px]">
                Payment history
              </h2>
              <p className="mt-0.5 text-portal-tiny text-portal-muted">
                {payments.length} payment{payments.length === 1 ? "" : "s"}{" "}
                recorded
              </p>
            </div>

            {payments.length === 0 ? (
              <div className="px-6 py-6">
                <EmptyState
                  icon={<ClockIcon aria-hidden className="size-6" />}
                  title="No payments yet"
                  message="Payments appear here once processed."
                />
              </div>
            ) : (
              <div className="divide-y divide-portal-line">
                {payments.map((tx) => (
                  <div
                    key={tx.id}
                    className="flex items-center justify-between px-6 py-3"
                  >
                    <div>
                      <p className="text-portal-small font-bold">{tx.type}</p>
                      <p className="text-portal-micro text-portal-muted">
                        {formatDate(tx.date)}
                      </p>
                    </div>
                    <span className="text-portal-small font-bold tabular-nums">
                      {formatCurrency(tx.amount)}
                    </span>
                  </div>
                ))}
                <div className="flex items-center justify-between bg-portal-bg px-6 py-3">
                  <span className="text-portal-small font-bold">
                    Total paid
                  </span>
                  <span className="text-portal-small font-bold text-portal-success tabular-nums">
                    {formatCurrency(schedule.amountPaid)}
                  </span>
                </div>
              </div>
            )}
          </Panel>
        </div>
      </div>
    </div>
  );
}
